import { Send } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import { PLANNING_ERROR, PLANNING_ERROR_MESSAGE } from '@obraplan/core/domain/planning/errors';
import type { DailyReportTask } from '@obraplan/core/domain/planning/daily-report-task';
import { quarterHoursFromDecimal } from '@obraplan/core/domain/planning/weekly-allocation';
import { isAreaUnitCode } from '@obraplan/core/domain/schedule/unit-of-measure';
import type { NonConformity } from '@obraplan/core/domain/quality/non-conformity';
import type {
  DayQualityCatalogService,
  DayQualityFrontView,
  DayQualityProjectFront,
} from '@obraplan/core/application/use-cases/quality/quality-use-cases';
import type { JornadaDoDia } from './horas-da-fila';
import { FRENTE_REPETIDA } from './frentes-do-dia';
import { Button } from '@/components/ui/button';
import { Field, Input, Textarea } from '@/components/ui/input';
import { AreaMeasurementButton } from './AreaMeasurementDialog';
import { ReportSection, SELECT_CLASS } from './DailyReportSection';
import { PecasDoDia, type CarregarPecas } from './PecasDoDia';
import { ComodosDoDia, type CarregarComodos } from './ComodosDoDia';
import type { FrontRoomsView } from '@obraplan/core/application/contracts/takeoff';
import { roomPlaceLabel } from '@obraplan/core/domain/quality/non-conformity-place';
import { chaveDoComodo } from '@/features/room-progress/planta-pintada';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  lerPercentual,
  mudancasDoRascunho,
  rascunhoVazio,
  type MudancasDosComodos,
  type RascunhoDosComodos,
} from '@/features/room-progress/rascunho-dos-comodos';
import { WorkQualityDialog } from './WorkQualityDialog';
import type { PendingNonConformityPhotos, VerificationInput } from './quality-verification-draft';

/**
 * "Mão de obra do dia" — quem trabalhou, em quê, por quanto tempo e com que qualidade
 * (Fase 20).
 *
 * É a primeira seção do RDO porque é a primeira pergunta do dia: antes do tempo, do
 * efetivo e das fotos, o responsável diz **quem fez o quê**. Os cartões da conferência
 * (`conference`) moram aqui dentro — são eles que confirmam, registram ausência, ajustam
 * horas, medem e fotografam quem veio do planejamento. O que ninguém planejou é lançado
 * logo abaixo — funcionário, atividade, quantidade e horas —, e vira alocação do dia e
 * conferência no mesmo gesto (ADR-131). Quando a atividade tem serviço cadastrado, o
 * modal da checklist abre antes de gravar (RN-226): produção sem verificação não entra.
 *
 * A atividade que **não está no banco** — descarregar vigota, limpar o canteiro — é
 * escrita à mão e vira tarefa avulsa (RN-213), com quem fez, quanto e por quanto tempo
 * como texto do dia. Ela não passa pela checklist porque não tem frente nem serviço.
 *
 * Componente puro: recebe o dia lido e devolve o que a pessoa lançou. Quem lê e grava
 * é `DailyReportWorkSection`; quem monta os cartões é a página, que tem os diálogos deles.
 */

export interface WorkResourceOption {
  readonly resourceId: string;
  readonly name: string;
  readonly detail: string;
}

export interface ReportWorkInput {
  readonly resourceId: string;
  readonly activityId: string;
  /** O serviço lançado. Ausente na frente cuja disciplina não tem serviço cadastrado. */
  readonly serviceId?: string;
  readonly quarterHours: number;
  readonly executedQuantity?: number;
  readonly verifications: readonly VerificationInput[];
  /** As fotos escolhidas nos itens reprovados, para subir depois de gravar (Fase 37). */
  readonly photos?: readonly PendingNonConformityPhotos[];
  /** O motivo da hora extra, quando as horas passam da jornada (RN-045). */
  readonly justification?: string;
  /**
   * As peças marcadas no dia (Fase 27, etapa 6), presente só quando a pessoa mexeu na lista:
   * quem grava as marca **antes** da quantidade (RN-381), e uma recusa para o lançamento.
   */
  readonly pieceNames?: readonly string[];
  /**
   * O avanço por cômodo do dia (Fase 34, etapa 2), presente só quando mudou algo: o "não se
   * aplica" e o percentual de cada apto × cômodo. Não é quantidade (D8): vai à parte.
   */
  readonly roomChanges?: MudancasDosComodos;
  /** A frente errada que este lançamento troca (RN-645): ela sai na mesma gravação. */
  readonly replaces?: { readonly allocationId: string; readonly expectedVersion: number };
}

/**
 * A atividade lançada errada, à espera da certa (RN-645). Vem do "Trocar" do dia da pessoa e
 * enche o formulário com o que já estava lá — a pessoa, o pavimento, as horas, a quantidade —,
 * para só a atividade ser escolhida de novo.
 */
export interface TrocaDeAtividade {
  readonly allocationId: string;
  readonly expectedVersion: number;
  readonly resourceId: string;
  readonly resourceName: string;
  readonly activityId: string;
  readonly serviceId?: string;
  /** "Alvenaria — 3º Pavimento · VERGAS / CONTRAVERGAS": a que sai, para o aviso. */
  readonly label: string;
  readonly quarterHours: number;
  readonly executedQuantity?: number;
}

/** Uma equipe que pode ser lançada no dia, com quem está nela naquela data (RN-053). */
export interface WorkTeamOption {
  readonly teamId: string;
  readonly name: string;
  readonly members: readonly WorkResourceOption[];
}

/** O lançamento da equipe (Fase 30, etapa 2 — D6, RN-421). */
export interface ReportTeamWorkInput {
  readonly teamId: string;
  readonly activityId: string;
  readonly serviceId: string;
  readonly members: readonly { readonly resourceId: string; readonly quarterHours: number }[];
  /** A quantidade da equipe — uma só. Ausente: só as horas; a equipe é medida depois. */
  readonly quantity?: number;
  readonly verifications: readonly VerificationInput[];
  /** As fotos escolhidas nos itens reprovados, para subir depois de gravar (Fase 37). */
  readonly photos?: readonly PendingNonConformityPhotos[];
  readonly justification?: string;
  readonly pieceNames?: readonly string[];
  readonly roomChanges?: MudancasDosComodos;
}

export interface DailyReportWorkProps {
  /** Os cartões da conferência do dia, com filtros — montados pela página. */
  conference: ReactNode;
  resources: readonly WorkResourceOption[];
  fronts: readonly DayQualityProjectFront[];
  /**
   * A jornada e as horas do dia de cada pessoa: a justificativa da hora extra só aparece
   * quando o lançamento passa da jornada (RN-045). Ausente = sem a leitura, e sem o campo.
   * As horas já trazem o que a fila do aparelho ainda vai gravar (RN-641).
   */
  workdays?: readonly JornadaDoDia[];
  /** O cadastro de serviços que a obra alcança — a lista do select de atividade. */
  serviceCatalog: readonly DayQualityCatalogService[];
  /** A frente inteira, com fichas e verificação, quando já lida. */
  frontDetails: (activityId: string) => DayQualityFrontView | undefined;
  nonConformities: readonly NonConformity[];
  /** De quem é cada NC do dia (Fase 37) — o nome ao lado do número, na checklist. */
  nonConformityOwners?: Readonly<Record<string, string>> | undefined;
  /** As NCs abertas de outros dias nas frentes — o aviso da remedição (Fase 37, D10). */
  pendingNonConformities?: readonly NonConformity[] | undefined;
  /** As avulsas já gravadas — a nova entra no fim da lista, que vai inteira. */
  tasks: readonly DailyReportTask[];
  editable: boolean;
  isPending: boolean;
  errorMessage?: string | undefined;
  /** Avisa qual frente foi escolhida, para quem lê buscar as fichas dela. */
  onSelectFront: (activityId: string | undefined) => void;
  /** O apontamento por peça: a atividade que mede peça mostra as do dia. Ausente = sem peças. */
  pieces?: { readonly date: string; readonly load: CarregarPecas } | undefined;
  /** O avanço por cômodo (Fase 34): o serviço que mede por ambiente mostra os cômodos. */
  rooms?: { readonly date: string; readonly load: CarregarComodos } | undefined;
  onReport: (input: ReportWorkInput) => Promise<boolean>;
  /**
   * As equipes do dia, na obra por equipe (Fase 30, etapa 2): o lançamento ganha "Por equipe" —
   * a equipe, quem trabalhou com as horas de cada um, e uma quantidade, a da equipe. Ausente ou
   * vazio = o formulário de sempre, por pessoa.
   */
  teams?: readonly WorkTeamOption[] | undefined;
  onReportTeam?: ((input: ReportTeamWorkInput) => Promise<boolean>) | undefined;
  /**
   * Se a pessoa já tem esta frente × serviço no dia, no servidor ou na fila (RN-643). O
   * lançamento repetido para no formulário, com a mensagem do servidor. Ausente = não confere.
   */
  hasFront?:
    | ((resourceId: string, activityId: string, serviceId: string | undefined) => boolean)
    | undefined;
  onAddTask: (tasks: readonly DailyReportTask[]) => Promise<boolean>;
  /**
   * A troca em curso (RN-645): o formulário vira "Trocar a atividade", com a pessoa fixa. Aparece
   * mesmo com o RDO emitido — a troca corrige a conferência, e o RDO fica desatualizado.
   */
  troca?: TrocaDeAtividade | undefined;
  /** A troca acabou — gravada ou cancelada. */
  onEncerrarTroca?: (() => void) | undefined;
}

const OUTRA = '__outra__';

/** A mesma mensagem do servidor (RN-645), dita antes de abrir a checklist. */
const TROCA_PELA_MESMA =
  PLANNING_ERROR_MESSAGE[PLANNING_ERROR.REPORTED_WORK_SAME_FRONT] ??
  'É a mesma atividade que já está lançada. Escolha a certa.';

/** `2º` antes de `10º` — a ordem alfabética inverte os dois, e a obra lê por número. */
function porNumero(a: string, b: string): number {
  return a.localeCompare(b, 'pt-BR', { numeric: true });
}

function distintos(valores: readonly string[]): string[] {
  return [...new Set(valores)].sort(porNumero);
}

/**
 * Uma opção do select de atividade.
 *
 * A atividade que o canteiro fala é o **serviço** — "ASSENTAMENTO DE ALVENARIA", "FÔRMA" —,
 * e ele é o mesmo em toda obra: sai do catálogo que as fichas de verificação apontam
 * (Configurações → Checklists). O que o sistema aloca continua sendo a frente
 * (pavimento × disciplina, ADR-008), e é por isso que a opção carrega as duas coisas: o
 * `activityId` da frente daquele pavimento e o nome do serviço escolhido.
 *
 * A disciplina sem serviço no catálogo — Elétrica e Hidráulica têm nomes próprios na
 * planilha da construtora — entra com o nome dela mesma, para não ficar sem caminho.
 */
interface OpcaoDeAtividade {
  readonly valor: string;
  readonly activityId: string;
  /** O serviço do cadastro. Ausente na frente cuja disciplina não tem serviço nenhum. */
  readonly serviceId?: string;
  readonly disciplina: string;
  readonly rotulo: string;
}

function opcoesDeAtividade(
  frentes: readonly DayQualityProjectFront[],
  catalogo: readonly DayQualityCatalogService[],
): readonly OpcaoDeAtividade[] {
  return frentes.flatMap((frente) => {
    const servicos = catalogo.filter(
      (servico) => normalizeName(servico.disciplineName) === normalizeName(frente.disciplineName),
    );
    if (servicos.length === 0) {
      return [
        {
          valor: `${frente.activityId}|`,
          activityId: frente.activityId,
          disciplina: frente.disciplineName,
          rotulo: frente.disciplineName,
        },
      ];
    }
    return servicos.map((servico): OpcaoDeAtividade => ({
      valor: `${frente.activityId}|${servico.serviceId}`,
      activityId: frente.activityId,
      serviceId: servico.serviceId,
      disciplina: frente.disciplineName,
      rotulo: servico.name,
    }));
  });
}

interface DialogState {
  readonly activityId: string;
  readonly serviceId?: string;
  readonly quantity: string;
  readonly unitName?: string;
  /** O código e as casas da unidade: a checklist também oferece a medida no m² (RN-379). */
  readonly unitCode?: string;
  readonly unitDecimals?: number;
  readonly quarterHours: number;
  /** O lançamento da equipe: quem trabalhou, com as horas de cada um. Ausente = por pessoa. */
  readonly members?: readonly { readonly resourceId: string; readonly quarterHours: number }[];
}

/** As horas digitadas em quartos, ou zero quando não são um número válido. */
function quartosOuZero(texto: string): number {
  const limpo = texto.trim().replace(',', '.');
  if (limpo === '') return 0;
  const parsed = quarterHoursFromDecimal(Number(limpo));
  return parsed.ok ? parsed.value : 0;
}

/** A quantidade digitada, ou ausente quando o campo ficou em branco. */
function parseQuantity(text: string): number | undefined {
  const limpo = text.trim().replace(',', '.');
  if (limpo === '') return undefined;
  const numero = Number(limpo);
  return Number.isFinite(numero) ? numero : undefined;
}

export function DailyReportWork({
  conference,
  resources,
  workdays,
  fronts,
  serviceCatalog,
  frontDetails,
  nonConformities,
  nonConformityOwners,
  pendingNonConformities,
  tasks,
  editable,
  isPending,
  errorMessage,
  onSelectFront,
  pieces,
  rooms,
  onReport,
  teams,
  onReportTeam,
  hasFront,
  onAddTask,
  troca,
  onEncerrarTroca,
}: DailyReportWorkProps) {
  const [resourceId, setResourceId] = useState('');
  const [torre, setTorre] = useState('');
  const [pavimento, setPavimento] = useState('');
  const [atividade, setAtividade] = useState('');
  const [descricao, setDescricao] = useState('');
  const [quantidadeTexto, setQuantidadeTexto] = useState('');
  // O que as peças puseram na quantidade: desmarcar todas zera só esse número.
  const [doPreenchimento, setDoPreenchimento] = useState<string | undefined>(undefined);
  const [unidade, setUnidade] = useState('');
  const [horasTexto, setHorasTexto] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [aviso, setAviso] = useState<string | undefined>(undefined);
  const [dialog, setDialog] = useState<DialogState | undefined>(undefined);
  // As peças marcadas, presas à frente × serviço em que foram marcadas: trocar de atividade
  // as invalida sem precisar lembrar de limpá-las.
  const [marcadas, setMarcadas] = useState<
    { readonly chave: string; readonly ids: readonly string[] } | undefined
  >(undefined);
  // O avanço por cômodo, preso à frente × serviço como as peças, com a leitura contra a qual o
  // rascunho se compara ao salvar.
  const [comodos, setComodos] = useState<
    | {
        readonly chave: string;
        readonly view: FrontRoomsView;
        readonly rascunho: RascunhoDosComodos;
      }
    | undefined
  >(undefined);
  // O lançamento por equipe (Fase 30, etapa 2): a equipe, e de cada membro se trabalhou e as
  // horas dele quando não foram as de todos. Sem entrada no mapa = marcado, com as horas gerais.
  const [porEquipe, setPorEquipe] = useState(false);
  const [teamId, setTeamId] = useState('');
  const [membros, setMembros] = useState<
    Readonly<Record<string, { readonly marcado: boolean; readonly horas: string }>>
  >({});

  // A troca (RN-645) enche o formulário com o lançamento errado — menos a atividade, que é o que
  // se escolhe de novo —, e o traz para a vista: o "Trocar" foi tocado no diálogo, que fechou.
  const formulario = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (troca === undefined) return;
    const errada = fronts.find((item) => item.activityId === troca.activityId);
    setPorEquipe(false);
    setResourceId(troca.resourceId);
    setTorre(errada?.towerName ?? '');
    setPavimento(errada?.floorName ?? '');
    setAtividade('');
    setDescricao('');
    setQuantidadeTexto(troca.executedQuantity === undefined ? '' : String(troca.executedQuantity));
    setDoPreenchimento(undefined);
    setUnidade('');
    setHorasTexto(String(troca.quarterHours / 4));
    setJustificativa('');
    setMarcadas(undefined);
    setComodos(undefined);
    setAviso(undefined);
    onSelectFront(undefined);
    formulario.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    // Só quando a troca muda: as frentes relidas não podem desfazer o que a pessoa já escolheu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [troca?.allocationId]);

  const temEquipes =
    teams !== undefined && teams.length > 0 && onReportTeam !== undefined && troca === undefined;
  const lancaEquipe = temEquipes && porEquipe;
  const equipe = lancaEquipe ? teams.find((item) => item.teamId === teamId) : undefined;
  const marcadosDaEquipe = (equipe?.members ?? []).filter(
    (membro) => membros[membro.resourceId]?.marcado ?? true,
  );
  /** As horas do membro: as dele, quando digitadas; senão as gerais. */
  const horasDe = (resourceId: string): string => {
    const proprias = membros[resourceId]?.horas.trim() ?? '';
    return proprias === '' ? horasTexto : proprias;
  };

  // A opção leva frente e serviço juntos; o que se aloca é a frente.
  const activityId = atividade === OUTRA ? OUTRA : (atividade.split('|')[0] ?? '');
  /** O serviço escolhido — vazio na frente cuja disciplina não tem serviço cadastrado. */
  const serviceId = atividade === OUTRA ? '' : (atividade.split('|')[1] ?? '');
  const livre = atividade === OUTRA;
  const frente = livre ? undefined : fronts.find((item) => item.activityId === activityId);
  /*
   * A unidade da medição vem do **serviço escolhido** (RN-232).
   *
   * Ela era da frente, e quando desceu para o serviço o campo de quantidade sumiu de toda
   * disciplina com mais de um serviço — que são quase todas. A frente não tem como saber se
   * o dia foi de assentamento em m² ou de verga em unidade; o serviço sabe.
   */
  const servicoEscolhido =
    serviceId === '' ? undefined : serviceCatalog.find((item) => item.serviceId === serviceId);
  /*
   * O serviço em m² — alvenaria, chapisco, emboço, reboco, gesso, drywall — ganha o botão
   * da produção pela medida (RN-379): altura × largura de cada pano, somadas no modal, e o
   * total cai neste campo. É a unidade que decide, e não o nome do serviço.
   */
  const pelaMedida = servicoEscolhido !== undefined && isAreaUnitCode(servicoEscolhido.unitCode);

  /*
   * Torre, pavimento e atividade em três listas encadeadas, na ordem em que a obra é
   * falada. Numa obra de duas torres com treze pavimentos e seis disciplinas, uma lista
   * só teria cento e cinquenta e seis linhas; assim a última tem seis.
   *
   * **A torre some quando é uma só.** Obrigar a escolher o que não tem alternativa é
   * pedir um clique para confirmar o óbvio — e a maioria das obras tem uma torre.
   */
  const torres = distintos(fronts.map((item) => item.towerName));
  const torreUnica = torres.length === 1 ? torres[0] : undefined;
  const torreEscolhida = torreUnica ?? (torres.includes(torre) ? torre : '');

  const frentesDaTorre = fronts.filter((item) => item.towerName === torreEscolhida);
  const pavimentos = distintos(frentesDaTorre.map((item) => item.floorName));
  const frentesDoPavimento = frentesDaTorre
    .filter((item) => item.floorName === pavimento)
    .sort((a, b) => porNumero(a.label, b.label));
  const atividades = opcoesDeAtividade(frentesDoPavimento, serviceCatalog);
  const disciplinas = distintos(atividades.map((item) => item.disciplina));

  const escolherTorre = (valor: string): void => {
    setTorre(valor);
    setAviso(undefined);
    // O pavimento e a atividade eram de outra torre: mantê-los lançaria no lugar errado.
    setPavimento('');
    setAtividade('');
    onSelectFront(undefined);
  };

  const escolherPavimento = (valor: string): void => {
    setPavimento(valor);
    setAviso(undefined);
    setAtividade('');
    onSelectFront(undefined);
  };
  const recurso = resources.find((item) => item.resourceId === resourceId);

  /*
   * **A justificativa só quando passa da jornada** (RN-045, 14/10/2026). Ela ficava sempre
   * à vista, e parecia obrigatória em todo lançamento. A conta é a do servidor: as horas que
   * a pessoa já tem no dia, de todas as atividades, mais as que estão sendo lançadas. Sem
   * jornada (RN-047), nunca é hora extra. As que já tem incluem as da fila do aparelho
   * (RN-641): sem sinal, o servidor só as vê na volta, antes deste lançamento.
   */
  // Na troca, as horas da frente errada saem junto: não contam no que a pessoa já tem.
  const lida = workdays?.find((item) => item.resourceId === resourceId);
  const jornada =
    lida === undefined || troca === undefined || troca.resourceId !== resourceId
      ? lida
      : {
          ...lida,
          allocatedQuarterHours: Math.max(0, lida.allocatedQuarterHours - troca.quarterHours),
        };
  const horasLancadas = quartosOuZero(horasTexto);
  /** Passa da jornada quem já tinha horas no dia e, com as lançadas, fica acima dela. */
  const estoura = (resourceId: string, quartos: number): boolean => {
    const dia =
      resourceId === jornada?.resourceId
        ? jornada
        : workdays?.find((item) => item.resourceId === resourceId);
    return (
      dia?.capacityQuarterHours !== undefined &&
      dia.allocatedQuarterHours + quartos > dia.capacityQuarterHours
    );
  };
  // Na equipe, a conta é de cada um, com as horas dele (RN-044, RN-045).
  const quemEstoura = lancaEquipe
    ? marcadosDaEquipe.filter((membro) =>
        estoura(membro.resourceId, quartosOuZero(horasDe(membro.resourceId))),
      )
    : [];
  const passaDaJornada =
    !livre && (lancaEquipe ? quemEstoura.length > 0 : estoura(resourceId, horasLancadas));
  const horas = (quartos: number): string =>
    `${(quartos / 4).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}h`;
  /** Quanto das horas do dia ainda espera no aparelho — só quando soma (RN-641). */
  const naFila = (resourceId: string): number =>
    Math.max(0, workdays?.find((item) => item.resourceId === resourceId)?.queuedQuarterHours ?? 0);
  const contaAFila = quemEstoura.some((membro) => naFila(membro.resourceId) > 0)
    ? ', contando o que aguarda envio neste aparelho'
    : '';
  const jaTem =
    jornada === undefined
      ? ''
      : naFila(resourceId) > 0
        ? `Já tem ${horas(jornada.allocatedQuarterHours)} no dia (${horas(naFila(resourceId))} aguardando envio)`
        : `Já tem ${horas(jornada.allocatedQuarterHours)} no dia`;
  const explicacaoDaHoraExtra = lancaEquipe
    ? `Passa da jornada do dia para ${quemEstoura.map((membro) => membro.name).join(', ')}${contaAFila}. Escreva o motivo (RN-045).`
    : jornada?.capacityQuarterHours === undefined
      ? ''
      : jornada.allocatedQuarterHours > 0
        ? `${jaTem}; com estas, passa da jornada de ${horas(jornada.capacityQuarterHours)}. Escreva o motivo (RN-045).`
        : `Passa da jornada de ${horas(jornada.capacityQuarterHours)} do dia. Escreva o motivo (RN-045).`;
  const detalhes =
    frente !== undefined && frente.hasServices ? frontDetails(frente.activityId) : undefined;
  const esperandoChecklist = frente !== undefined && frente.hasServices && detalhes === undefined;

  const escolherAtividade = (valor: string): void => {
    setAtividade(valor);
    setAviso(undefined);
    const frente = valor === '' || valor === OUTRA ? undefined : valor.split('|')[0];
    onSelectFront(frente === '' ? undefined : frente);
  };

  /** As peças marcadas para esta frente × serviço, na forma que o lançamento leva. */
  const pecasMarcadas = (
    frenteId: string,
    servicoId: string | undefined,
  ): { pieceNames?: readonly string[] } =>
    marcadas !== undefined && marcadas.chave === `${frenteId}|${servicoId ?? ''}`
      ? { pieceNames: marcadas.ids }
      : {};

  /** O que o rascunho dos cômodos desta frente × serviço muda — ou o erro, ou nada. */
  const mudancasDosComodos = (frenteId: string, servicoId: string | undefined) =>
    comodos !== undefined &&
    comodos.chave === `${frenteId}|${servicoId ?? ''}` &&
    !rascunhoVazio(comodos.rascunho)
      ? mudancasDoRascunho(comodos.view, comodos.rascunho)
      : undefined;

  /** Os cômodos apontados, na forma que o lançamento leva; conferidos antes, em `lancar`. */
  const comodosApontados = (
    frenteId: string,
    servicoId: string | undefined,
  ): { roomChanges?: MudancasDosComodos } => {
    const mudancas = mudancasDosComodos(frenteId, servicoId);
    return mudancas !== undefined &&
      mudancas.ok &&
      (mudancas.value.rooms.length > 0 || mudancas.value.notApplicable.length > 0)
      ? { roomChanges: mudancas.value }
      : {};
  };

  /** O percentual fora da faixa para o lançamento antes de abrir a checklist. */
  const comodoInvalido = (frenteId: string, servicoId: string | undefined): string | undefined => {
    const mudancas = mudancasDosComodos(frenteId, servicoId);
    return mudancas !== undefined && !mudancas.ok ? mudancas.message : undefined;
  };

  const limpar = (): void => {
    setMarcadas(undefined);
    setComodos(undefined);
    setTorre('');
    setPavimento('');
    setAtividade('');
    setDescricao('');
    setQuantidadeTexto('');
    setUnidade('');
    setHorasTexto('');
    setJustificativa('');
    // A equipe escolhida fica: o próximo lançamento costuma ser dela de novo.
    setMembros({});
    setAviso(undefined);
    onSelectFront(undefined);
    onEncerrarTroca?.();
  };

  /** A frente errada vai junto, e sai na mesma gravação (RN-645). */
  const substitui = (): Pick<ReportWorkInput, 'replaces'> =>
    troca === undefined
      ? {}
      : { replaces: { allocationId: troca.allocationId, expectedVersion: troca.expectedVersion } };

  /** A chave só existe quando há motivo: o caso de uso distingue ausente de vazio. */
  const motivo = (): { justification?: string } =>
    !passaDaJornada || justificativa.trim() === '' ? {} : { justification: justificativa.trim() };

  /** As horas em quartos, ou o motivo de não serem. Vazio é permitido só na avulsa. */
  const quartos = (): { ok: true; value: number | undefined } | { ok: false; message: string } => {
    const texto = horasTexto.trim().replace(',', '.');
    if (texto === '') {
      return livre
        ? { ok: true, value: undefined }
        : { ok: false, message: 'Informe as horas gastas na atividade.' };
    }
    const parsed = quarterHoursFromDecimal(Number(texto));
    if (!parsed.ok) return { ok: false, message: parsed.error.message };
    return { ok: true, value: parsed.value };
  };

  /** As horas de cada membro marcado, em quartos, ou o motivo de não serem. */
  const horasDaEquipe = ():
    | { ok: true; value: { resourceId: string; quarterHours: number }[] }
    | { ok: false; message: string } => {
    const lista: { resourceId: string; quarterHours: number }[] = [];
    for (const membro of marcadosDaEquipe) {
      const texto = horasDe(membro.resourceId).trim().replace(',', '.');
      if (texto === '') return { ok: false, message: `Informe as horas de ${membro.name}.` };
      const parsed = quarterHoursFromDecimal(Number(texto));
      if (!parsed.ok) return { ok: false, message: `${membro.name}: ${parsed.error.message}` };
      lista.push({ resourceId: membro.resourceId, quarterHours: parsed.value });
    }
    return { ok: true, value: lista };
  };

  const lancarEquipe = async (): Promise<void> => {
    if (equipe === undefined || onReportTeam === undefined) {
      setAviso('Escolha a equipe.');
      return;
    }
    if (marcadosDaEquipe.length === 0) {
      setAviso('Marque quem da equipe trabalhou.');
      return;
    }

    if (livre) {
      // A avulsa da equipe é a mesma tarefa do dia, com quem trabalhou nela.
      const texto = descricao.trim();
      if (texto === '') {
        setAviso('Escreva a atividade que não está no banco.');
        return;
      }
      const geral = quartos();
      if (!geral.ok) {
        setAviso(geral.message);
        return;
      }
      const numero = parseQuantity(quantidadeTexto);
      const unidadeTexto = unidade.trim();
      const nova: DailyReportTask = {
        description: texto,
        workers: marcadosDaEquipe.map((membro) => membro.name),
        ...(numero === undefined ? {} : { quantity: numero }),
        ...(unidadeTexto === '' ? {} : { unit: unidadeTexto }),
        ...(geral.value === undefined ? {} : { quarterHours: geral.value }),
      };
      if (await onAddTask([...tasks, nova])) limpar();
      return;
    }

    if (frente === undefined) {
      setAviso('Escolha a atividade.');
      return;
    }
    if (serviceId === '') {
      setAviso(
        'Esta frente não tem serviço cadastrado, e sem serviço não há medição da equipe. Lance por pessoa.',
      );
      return;
    }
    const horasDosMembros = horasDaEquipe();
    if (!horasDosMembros.ok) {
      setAviso(horasDosMembros.message);
      return;
    }
    const invalidoDaEquipe = comodoInvalido(frente.activityId, serviceId);
    if (invalidoDaEquipe !== undefined) {
      setAviso(invalidoDaEquipe);
      return;
    }
    // RN-643 — quem já tem esta frente × serviço no dia (no servidor ou na fila) para aqui,
    // com o nome na frente, como o servidor diria na volta.
    const repetido = horasDosMembros.value.find((membro) =>
      hasFront?.(membro.resourceId, frente.activityId, serviceId),
    );
    if (repetido !== undefined) {
      const nome =
        marcadosDaEquipe.find((membro) => membro.resourceId === repetido.resourceId)?.name ?? '';
      setAviso(nome === '' ? FRENTE_REPETIDA : `${nome}: ${FRENTE_REPETIDA}`);
      return;
    }

    if (frente.hasServices) {
      setDialog({
        activityId: frente.activityId,
        serviceId,
        quantity: quantidadeTexto,
        quarterHours: horasDosMembros.value.reduce((soma, item) => soma + item.quarterHours, 0),
        members: horasDosMembros.value,
        ...(servicoEscolhido?.unitName === undefined
          ? {}
          : { unitName: servicoEscolhido.unitName }),
        ...(servicoEscolhido?.unitCode === undefined
          ? {}
          : { unitCode: servicoEscolhido.unitCode }),
        ...(servicoEscolhido?.unitDecimals === undefined
          ? {}
          : { unitDecimals: servicoEscolhido.unitDecimals }),
      });
      return;
    }

    const numero = parseQuantity(quantidadeTexto);
    const ok = await onReportTeam({
      teamId: equipe.teamId,
      activityId: frente.activityId,
      serviceId,
      members: horasDosMembros.value,
      ...(numero === undefined ? {} : { quantity: numero }),
      verifications: [],
      ...pecasMarcadas(frente.activityId, serviceId),
      ...comodosApontados(frente.activityId, serviceId),
      ...motivo(),
    });
    if (ok) limpar();
  };

  const lancar = async (): Promise<void> => {
    setAviso(undefined);
    if (lancaEquipe) {
      await lancarEquipe();
      return;
    }
    if (recurso === undefined) {
      setAviso('Escolha o funcionário.');
      return;
    }
    const quarterHours = quartos();
    if (!quarterHours.ok) {
      setAviso(quarterHours.message);
      return;
    }

    if (livre) {
      const texto = descricao.trim();
      if (texto === '') {
        setAviso('Escreva a atividade que não está no banco.');
        return;
      }
      const numero = parseQuantity(quantidadeTexto);
      const unidadeTexto = unidade.trim();
      const nova: DailyReportTask = {
        description: texto,
        workers: [recurso.name],
        ...(numero === undefined ? {} : { quantity: numero }),
        ...(unidadeTexto === '' ? {} : { unit: unidadeTexto }),
        ...(quarterHours.value === undefined ? {} : { quarterHours: quarterHours.value }),
      };
      if (await onAddTask([...tasks, nova])) limpar();
      return;
    }

    if (frente === undefined) {
      setAviso('Escolha a atividade.');
      return;
    }
    if (quarterHours.value === undefined) return;
    // RN-645 — trocar pela mesma é não trocar: as horas e a quantidade se ajustam no dia.
    if (
      troca !== undefined &&
      troca.activityId === frente.activityId &&
      (troca.serviceId ?? '') === serviceId
    ) {
      setAviso(TROCA_PELA_MESMA);
      return;
    }
    const invalido = comodoInvalido(frente.activityId, serviceId === '' ? undefined : serviceId);
    if (invalido !== undefined) {
      setAviso(invalido);
      return;
    }
    // RN-643 — a mesma frente × serviço de novo para a mesma pessoa no dia: o servidor recusaria.
    if (
      hasFront?.(recurso.resourceId, frente.activityId, serviceId === '' ? undefined : serviceId)
    ) {
      setAviso(FRENTE_REPETIDA);
      return;
    }

    if (frente.hasServices) {
      // RN-226 — a checklist antes de gravar. O modal responde e lança de uma vez.
      setDialog({
        activityId: frente.activityId,
        ...(serviceId === '' ? {} : { serviceId }),
        quantity: quantidadeTexto,
        quarterHours: quarterHours.value,
        ...(servicoEscolhido?.unitName === undefined
          ? {}
          : { unitName: servicoEscolhido.unitName }),
        ...(servicoEscolhido?.unitCode === undefined
          ? {}
          : { unitCode: servicoEscolhido.unitCode }),
        ...(servicoEscolhido?.unitDecimals === undefined
          ? {}
          : { unitDecimals: servicoEscolhido.unitDecimals }),
      });
      return;
    }

    const numero = parseQuantity(quantidadeTexto);
    const ok = await onReport({
      resourceId: recurso.resourceId,
      activityId: frente.activityId,
      // O serviço é o que se fez; a frente é onde (RN-231). É dele que sai a unidade.
      ...(serviceId === '' ? {} : { serviceId }),
      quarterHours: quarterHours.value,
      ...(numero === undefined ? {} : { executedQuantity: numero }),
      verifications: [],
      ...pecasMarcadas(frente.activityId, serviceId),
      ...comodosApontados(frente.activityId, serviceId),
      ...motivo(),
      ...substitui(),
    });
    if (ok) limpar();
  };

  const confirmarDialogo = async (
    verifications: readonly VerificationInput[],
    photos: readonly PendingNonConformityPhotos[],
  ): Promise<void> => {
    if (dialog === undefined) return;
    const numero = parseQuantity(dialog.quantity);
    if (dialog.members !== undefined) {
      if (equipe === undefined || onReportTeam === undefined || dialog.serviceId === undefined) {
        return;
      }
      const lancado = await onReportTeam({
        teamId: equipe.teamId,
        activityId: dialog.activityId,
        serviceId: dialog.serviceId,
        members: dialog.members,
        ...(numero === undefined ? {} : { quantity: numero }),
        verifications,
        ...(photos.length === 0 ? {} : { photos }),
        ...pecasMarcadas(dialog.activityId, dialog.serviceId),
        ...comodosApontados(dialog.activityId, dialog.serviceId),
        ...motivo(),
      });
      if (lancado) {
        setDialog(undefined);
        limpar();
      }
      return;
    }
    if (recurso === undefined) return;
    const ok = await onReport({
      resourceId: recurso.resourceId,
      activityId: dialog.activityId,
      ...(dialog.serviceId === undefined ? {} : { serviceId: dialog.serviceId }),
      quarterHours: dialog.quarterHours,
      ...(numero === undefined ? {} : { executedQuantity: numero }),
      verifications,
      ...(photos.length === 0 ? {} : { photos }),
      ...pecasMarcadas(dialog.activityId, dialog.serviceId),
      ...comodosApontados(dialog.activityId, dialog.serviceId),
      ...motivo(),
      ...substitui(),
    });
    if (ok) {
      setDialog(undefined);
      limpar();
    }
  };

  const frenteDoDialogo = dialog === undefined ? undefined : frontDetails(dialog.activityId);
  // As peças e os cômodos que este lançamento marcou e ainda não gravou: a NC pode apontá-los
  // (Fase 37, D9). Só os da frente × serviço do modal, e o cômodo só com percentual válido.
  const chaveDoDialogo =
    dialog?.serviceId === undefined ? undefined : `${dialog.activityId}|${dialog.serviceId}`;
  const lugaresDoRascunho =
    chaveDoDialogo === undefined
      ? undefined
      : {
          ...(marcadas?.chave === chaveDoDialogo ? { pieces: marcadas.ids } : {}),
          ...(comodos?.chave === chaveDoDialogo
            ? {
                rooms: comodos.view.rooms.flatMap((room) => {
                  const texto = comodos.rascunho.percentuais[chaveDoComodo(room)];
                  const valor = texto === undefined ? undefined : lerPercentual(texto);
                  return typeof valor === 'number'
                    ? [
                        {
                          positionId: room.positionId,
                          roomId: room.roomId,
                          label: roomPlaceLabel({
                            roomName: room.roomName,
                            positionLabel: room.positionLabel,
                          }),
                        },
                      ]
                    : [];
                }),
              }
            : {}),
        };
  const rotuloDoDialogo =
    dialog === undefined
      ? ''
      : (fronts.find((item) => item.activityId === dialog.activityId)?.label ?? '');

  return (
    <ReportSection
      title="Mão de obra do dia"
      description="Quem trabalhou, em quê, por quanto tempo e com que qualidade. Quem veio do planejamento é conferido nos cartões; quem ninguém planejou é lançado abaixo e vira conferência no mesmo gesto."
      editable={editable}
      isSaving={false}
    >
      {conference}

      {(editable || troca !== undefined) && (
        <div
          ref={formulario}
          className="flex scroll-mt-4 flex-col gap-3 rounded-md border border-(--color-border-subtle) p-3"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-(--color-content-muted)">
              {troca === undefined ? 'Lançar trabalho' : 'Trocar a atividade'}
            </p>
            {/* Só na obra por equipe: lá a quantidade de quem está numa equipe é da equipe. */}
            {temEquipes && (
              <div role="group" aria-label="Lançar por" className="flex gap-1">
                {(
                  [
                    { valor: false, rotulo: 'Por pessoa' },
                    { valor: true, rotulo: 'Por equipe' },
                  ] as const
                ).map((opcao) => (
                  <button
                    key={opcao.rotulo}
                    type="button"
                    aria-pressed={porEquipe === opcao.valor}
                    disabled={isPending}
                    onClick={() => {
                      setPorEquipe(opcao.valor);
                      setAviso(undefined);
                    }}
                    className={`min-h-11 rounded-md px-3 py-1.5 text-sm md:min-h-9 ${
                      porEquipe === opcao.valor
                        ? 'bg-(--color-surface-muted) font-medium text-(--color-content)'
                        : 'text-(--color-content-muted) hover:bg-(--color-surface-muted)'
                    }`}
                  >
                    {opcao.rotulo}
                  </button>
                ))}
              </div>
            )}
          </div>
          {troca !== undefined && (
            <div
              role="status"
              className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-(--color-surface-muted) px-3 py-2 text-sm"
            >
              <p>
                <strong>{troca.label}</strong> sai do dia de {troca.resourceName} quando a atividade
                certa for gravada.
              </p>
              <Button
                variant="ghost"
                size="sm"
                disabled={isPending}
                onClick={() => {
                  setAviso(undefined);
                  onEncerrarTroca?.();
                }}
              >
                Cancelar a troca
              </Button>
            </div>
          )}
          {/* `grid-cols-1` e não só `grid`: a coluna implícita é `auto`, e o select com o nome
              mais longo da obra a alargava além do cartão no celular — levando junto a
              quantidade e o botão da medida. `minmax(0, 1fr)` segura tudo na largura da tela. */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {lancaEquipe ? (
              <Field label="Equipe" htmlFor="rdo-trabalho-equipe" required>
                <select
                  id="rdo-trabalho-equipe"
                  className={SELECT_CLASS}
                  value={teamId}
                  disabled={isPending}
                  onChange={(event) => {
                    setTeamId(event.target.value);
                    setMembros({});
                    setAviso(undefined);
                  }}
                >
                  <option value="">Selecione…</option>
                  {teams.map((item) => (
                    <option key={item.teamId} value={item.teamId}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <Field label="Funcionário" htmlFor="rdo-trabalho-funcionario" required>
                <select
                  id="rdo-trabalho-funcionario"
                  className={SELECT_CLASS}
                  value={resourceId}
                  // Na troca a pessoa é a do lançamento errado: o servidor troca no dia dela.
                  disabled={isPending || troca !== undefined}
                  onChange={(event) => {
                    setResourceId(event.target.value);
                    setAviso(undefined);
                  }}
                >
                  <option value="">Selecione…</option>
                  {resources.map((item) => (
                    <option key={item.resourceId} value={item.resourceId}>
                      {item.name} — {item.detail}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {torreUnica === undefined && (
              <Field label="Torre" htmlFor="rdo-trabalho-torre">
                <select
                  id="rdo-trabalho-torre"
                  className={SELECT_CLASS}
                  value={torreEscolhida}
                  disabled={isPending}
                  onChange={(event) => escolherTorre(event.target.value)}
                >
                  <option value="">Selecione…</option>
                  {torres.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field
              label="Pavimento"
              htmlFor="rdo-trabalho-pavimento"
              {...(torreEscolhida === ''
                ? { hint: 'Escolha a torre para ver os pavimentos.' }
                : {})}
            >
              <select
                id="rdo-trabalho-pavimento"
                className={SELECT_CLASS}
                value={pavimento}
                disabled={isPending}
                onChange={(event) => escolherPavimento(event.target.value)}
              >
                <option value="">Selecione…</option>
                {pavimentos.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Atividade"
              htmlFor="rdo-trabalho-atividade"
              required
              {...(pavimento === ''
                ? { hint: 'Escolha o pavimento para ver as atividades dele.' }
                : {})}
            >
              {/*
                Pesquisa-se no próprio campo, como pediu o Gilvan (30/10/2026): o pavimento
                tem dezenas de serviços, e rolar o select no tablet era lento.
              */}
              <SearchableSelect
                id="rdo-trabalho-atividade"
                className={SELECT_CLASS}
                value={atividade}
                disabled={isPending}
                searchPlaceholder="Pesquisar o serviço…"
                groups={disciplinas.map((disciplina) => ({
                  label: disciplina,
                  options: atividades
                    .filter((item) => item.disciplina === disciplina)
                    .map((item) => ({ value: item.valor, label: item.rotulo })),
                }))}
                // A avulsa não tem pavimento, e por isso não depende do select de cima. Na troca
                // ela não entra: não é frente, e não teria como substituir uma.
                fixedOptions={
                  troca === undefined ? [{ value: OUTRA, label: 'Outra atividade (escrever)' }] : []
                }
                emptyText={(termo) => `Nenhum serviço deste pavimento com “${termo}”.`}
                onChange={escolherAtividade}
              />
            </Field>

            {livre && (
              <Field
                label="Descrição da atividade"
                htmlFor="rdo-trabalho-descricao"
                required
                hint="O que não está no banco vira tarefa avulsa: conta o dia, não entra em apuração."
              >
                <Input
                  id="rdo-trabalho-descricao"
                  value={descricao}
                  disabled={isPending}
                  placeholder="Descarregamento de vigota e EPS"
                  onChange={(event) => setDescricao(event.target.value)}
                />
              </Field>
            )}

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {(livre || servicoEscolhido?.unitName !== undefined) && (
                // Com o botão da medida ao lado, o campo ocupa duas colunas: numa só, o
                // botão comia o espaço e o número ficava num fio de 50px.
                <div className={pelaMedida ? 'col-span-2' : undefined}>
                  <Field
                    label={
                      livre
                        ? 'Quantidade'
                        : `${lancaEquipe ? 'Quantidade da equipe' : 'Quantidade'} (${servicoEscolhido?.unitName ?? ''})`
                    }
                    htmlFor="rdo-trabalho-quantidade"
                    {...(pelaMedida
                      ? { hint: 'Ou calcule pela medida: altura × largura de cada pano.' }
                      : {})}
                  >
                    <div className="flex gap-2">
                      <Input
                        id="rdo-trabalho-quantidade"
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="any"
                        value={quantidadeTexto}
                        disabled={isPending}
                        onChange={(event) => setQuantidadeTexto(event.target.value)}
                      />
                      {pelaMedida && (
                        <AreaMeasurementButton
                          subject={servicoEscolhido.name}
                          unitName={servicoEscolhido.unitName ?? 'm²'}
                          decimals={servicoEscolhido.unitDecimals ?? 2}
                          disabled={isPending}
                          onApply={(total) => setQuantidadeTexto(String(total))}
                        />
                      )}
                    </div>
                  </Field>
                </div>
              )}
              {livre && (
                <Field label="Unidade" htmlFor="rdo-trabalho-unidade">
                  <Input
                    id="rdo-trabalho-unidade"
                    value={unidade}
                    disabled={isPending}
                    placeholder="un, m², sc"
                    onChange={(event) => setUnidade(event.target.value)}
                  />
                </Field>
              )}
              <Field
                label="Horas gastas"
                htmlFor="rdo-trabalho-horas"
                required={!livre && !lancaEquipe}
                {...(lancaEquipe ? { hint: 'Valem para todos os marcados.' } : {})}
              >
                <Input
                  id="rdo-trabalho-horas"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={0.25}
                  value={horasTexto}
                  disabled={isPending}
                  onChange={(event) => setHorasTexto(event.target.value)}
                />
              </Field>
            </div>
          </div>

          {equipe !== undefined && (
            <fieldset className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3">
              <legend className="px-1 text-sm font-medium">Quem trabalhou</legend>
              {equipe.members.length === 0 ? (
                <p className="text-sm text-(--color-content-muted)">
                  Ninguém está nesta equipe neste dia.
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {equipe.members.map((membro) => {
                    const estado = membros[membro.resourceId];
                    const marcado = estado?.marcado ?? true;
                    return (
                      <li key={membro.resourceId} className="flex flex-wrap items-center gap-3">
                        <label className="flex min-h-9 min-w-0 flex-1 items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={marcado}
                            disabled={isPending}
                            onChange={() =>
                              setMembros((atual) => ({
                                ...atual,
                                [membro.resourceId]: {
                                  marcado: !marcado,
                                  horas: atual[membro.resourceId]?.horas ?? '',
                                },
                              }))
                            }
                          />
                          <span className="min-w-0 break-words">
                            {membro.name}
                            <span className="text-(--color-content-muted)"> — {membro.detail}</span>
                          </span>
                        </label>
                        <Input
                          className="w-24"
                          type="number"
                          inputMode="decimal"
                          min={0}
                          step={0.25}
                          aria-label={`Horas de ${membro.name}`}
                          placeholder={horasTexto === '' ? 'h' : horasTexto}
                          value={estado?.horas ?? ''}
                          disabled={isPending || !marcado}
                          onChange={(event) =>
                            setMembros((atual) => ({
                              ...atual,
                              [membro.resourceId]: { marcado, horas: event.target.value },
                            }))
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className="text-xs text-(--color-content-muted)">
                As horas gastas valem para todos; mude só a de quem fez diferente. A quantidade é
                uma só, a da equipe.
              </p>
            </fieldset>
          )}

          {pieces !== undefined &&
            !livre &&
            frente !== undefined &&
            serviceId !== '' &&
            servicoEscolhido?.unitName !== undefined && (
              <PecasDoDia
                activityId={frente.activityId}
                serviceId={serviceId}
                date={pieces.date}
                load={pieces.load}
                unit={{
                  name: servicoEscolhido.unitName,
                  decimals: servicoEscolhido.unitDecimals ?? 2,
                }}
                selected={
                  marcadas?.chave === `${frente.activityId}|${serviceId}` ? marcadas.ids : undefined
                }
                disabled={isPending}
                onChange={({ pieceNames, total }) => {
                  setMarcadas({ chave: `${frente.activityId}|${serviceId}`, ids: pieceNames });
                  // A soma preenche a quantidade, que continua editável (RN-382). Sem peça
                  // marcada, o número que as peças puseram vai a zero; o digitado fica.
                  if (total !== undefined || quantidadeTexto === doPreenchimento) {
                    const preenchido = total === undefined ? '0' : String(total);
                    setQuantidadeTexto(preenchido);
                    setDoPreenchimento(preenchido);
                  }
                }}
              />
            )}

          {rooms !== undefined && !livre && frente !== undefined && serviceId !== '' && (
            <ComodosDoDia
              activityId={frente.activityId}
              serviceId={serviceId}
              date={rooms.date}
              load={rooms.load}
              unit={servicoEscolhido?.unitName}
              rascunho={
                comodos?.chave === `${frente.activityId}|${serviceId}`
                  ? comodos.rascunho
                  : undefined
              }
              disabled={isPending}
              onChange={({ view, rascunho }) =>
                setComodos({ chave: `${frente.activityId}|${serviceId}`, view, rascunho })
              }
            />
          )}

          {passaDaJornada && (
            <Field
              label="Justificativa da hora extra"
              htmlFor="rdo-trabalho-justificativa"
              required
              hint={explicacaoDaHoraExtra}
            >
              <Textarea
                id="rdo-trabalho-justificativa"
                rows={2}
                value={justificativa}
                disabled={isPending}
                onChange={(event) => setJustificativa(event.target.value)}
              />
            </Field>
          )}
          {frente !== undefined && frente.hasServices && (
            <p className="text-xs text-(--color-content-muted)">
              {frente.disciplineName} tem serviço cadastrado: a checklist abre ao lançar.
            </p>
          )}
          {aviso !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-risk)">
              {aviso}
            </p>
          )}
          {errorMessage !== undefined && dialog === undefined && (
            <p role="alert" className="text-sm text-(--color-state-risk)">
              {errorMessage}
            </p>
          )}

          <div>
            <Button
              disabled={
                isPending ||
                esperandoChecklist ||
                (lancaEquipe ? teamId === '' : resourceId === '') ||
                activityId === ''
              }
              onClick={() => void lancar()}
            >
              <Send aria-hidden="true" />
              {esperandoChecklist
                ? 'Carregando a checklist…'
                : troca === undefined
                  ? 'Lançar'
                  : 'Trocar a atividade'}
            </Button>
          </div>
        </div>
      )}

      <WorkQualityDialog
        open={dialog !== undefined}
        title="Verificação de qualidade"
        description={
          rotuloDoDialogo === ''
            ? 'O serviço executado e a checklist dele.'
            : `${rotuloDoDialogo} — o serviço executado e a checklist dele.`
        }
        front={frenteDoDialogo}
        {...(dialog?.serviceId === undefined ? {} : { serviceId: dialog.serviceId })}
        nonConformities={nonConformities}
        nonConformityOwners={nonConformityOwners}
        pendingNonConformities={pendingNonConformities}
        draftPlaces={lugaresDoRascunho}
        {...(dialog !== undefined && dialog.unitName !== undefined
          ? {
              quantity: {
                value: dialog.quantity,
                unitName: dialog.unitName,
                ...(dialog.unitCode === undefined ? {} : { unitCode: dialog.unitCode }),
                ...(dialog.unitDecimals === undefined ? {} : { unitDecimals: dialog.unitDecimals }),
                onChange: (value: string) => setDialog({ ...dialog, quantity: value }),
              },
            }
          : {})}
        justification={
          passaDaJornada ? { value: justificativa, onChange: setJustificativa } : undefined
        }
        confirmLabel={troca === undefined ? 'Lançar trabalho' : 'Trocar a atividade'}
        isPending={isPending}
        errorMessage={errorMessage}
        onOpenChange={(open) => {
          if (!open) setDialog(undefined);
        }}
        onConfirm={(verifications, photos) => void confirmarDialogo(verifications, photos)}
      />
    </ReportSection>
  );
}
